/**
 * Folding `visibility_checks` rows into the two numbers anyone actually reads.
 *
 * ── Why this is in the shared package ────────────────────────────────────────
 * Two surfaces summarise this table — the morning brief on the backend and the
 * pipeline dashboard on the frontend — and they must agree, because they are
 * read by the same person hours apart. When the grouping lived in both, the
 * first divergence would be silent and would look like the number moving.
 * `config/` is the one package both sides already depend on, and its own header
 * says so: "used by both the frontend and backend".
 *
 * Everything here is pure and defensive. The rows arrive from a database driver
 * that returns `numeric` as a string, `null` for anything unset, and `text[]`
 * as an array that is occasionally absent — so nothing is trusted to have the
 * shape the collection promises.
 */
/** Our own name as `rankProducts` writes it. Never counted as a rival. */
const US = "Rebase";
/**
 * Summarise the latest run and the one before it, or null if nothing has run.
 *
 * `null` rather than a zeroed summary, deliberately. This table is new, so
 * "nothing has run yet" is the normal early state, and a confident `0 of 0`
 * reads as a measurement rather than an absence — which is the exact confusion
 * `brief.ts` exists to prevent everywhere else in this app.
 *
 * Runs are grouped by the UTC date they were asked on. That is exact here: the
 * cron fires weekly and writes all its rows inside a minute of itself. If it
 * ever runs more than once a day, this collapses those into one run — which is
 * the right answer for a trend line and the wrong one for an audit, and the
 * place to fix it would be a run id on the row rather than a finer timestamp.
 */
export function summariseVisibility(rows, options = {}) {
    const byDay = new Map();
    for (const row of rows ?? []) {
        const day = typeof row?.asked_at === "string" ? row.asked_at.slice(0, 10) : "";
        if (!day)
            continue;
        const bucket = byDay.get(day);
        if (bucket)
            bucket.push(row);
        else
            byDay.set(day, [row]);
    }
    const days = [...byDay.keys()].sort().reverse();
    if (days.length === 0)
        return null;
    const latest = byDay.get(days[0]) ?? [];
    const previous = days[1] ? byDay.get(days[1]) ?? [] : [];
    const tally = new Map();
    for (const row of latest) {
        const rivals = Array.isArray(row?.rivals) ? row.rivals : [];
        // One row can only vote once for a product, however many times the
        // answer named it — otherwise a single verbose answer outweighs three
        // questions, and the column stops being "how many questions surface
        // this competitor".
        for (const rival of new Set(rivals)) {
            if (typeof rival !== "string" || rival === US)
                continue;
            tally.set(rival, (tally.get(rival) ?? 0) + 1);
        }
    }
    return {
        day: days[0],
        asked: latest.length,
        named: latest.filter(row => row?.mentioned === true).length,
        prevAsked: previous.length,
        prevNamed: previous.filter(row => row?.mentioned === true).length,
        rivals: [...tally.entries()]
            // Count first, then name, so the order is stable across runs with
            // ties — a list that reshuffles on every load reads as movement.
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .slice(0, options.topRivals ?? 6)
            .map(([name, count]) => ({ name, count })),
        ungrounded: latest.filter(row => row?.grounded === false).length
    };
}
/**
 * Whole days between a summary's run and now. 0 means today.
 *
 * `floor`, not `round`. With `round`, a check that ran at 06:00 reads as
 * "1d ago" from 12:00 the same day — the brief would date this morning's
 * measurement to yesterday, which is the one thing the age suffix exists to
 * prevent.
 */
export function summaryAgeDays(summary, now = Date.now()) {
    const at = Date.parse(`${summary.day}T00:00:00Z`);
    if (Number.isNaN(at))
        return 0;
    return Math.max(0, Math.floor((now - at) / 86_400_000));
}
